/**
 * Where a scene's values name each `[ext_resource]`. The text loader waits on an `[ext_resource]`'s load only where a
 * value names it, and a failed load there raises `ERR_FILE_MISSING_DEPENDENCIES` (`resource_format_text.cpp:146-154`).
 * A node body skips that error (`:288-289`). Every other read returns it, and the load fails.
 */
import { extResourceIdsIn } from '../godot/variant/resourceRef.js';
/** Records each id `values` name as `use`, where no aborting use already decided it. */
function addIdsIn(values, use, into) {
    for (const value of values) {
        if (typeof value !== 'string')
            continue;
        for (const id of extResourceIdsIn(value)) {
            if (into.get(id) !== 'aborting')
                into.set(id, use);
        }
    }
}
function addNodeIds(node, into) {
    if (node.instance !== undefined)
        addIdsIn([node.instance], 'node', into);
    addIdsIn(Object.values(node.rawProperties), 'node', into);
    for (const child of node.children)
        addNodeIds(child, into);
}
/**
 * Each `[ext_resource]` id the scene's values name, and where. Aborting: a sub-resource's value, a `.tres` file's
 * `[resource]` value, a `[connection]` heading's `binds=`, and the `instance=` of a node heading that follows no other
 * node (`StrictScene.instancesOutsideNodeBody`). A heading's fields parse through the same callback as a value
 * (`variant_parser.cpp:1861-1866`). A binds failure inside a node body leaves the rest of the heading unread, and the
 * `parse_tag` after it fails (`resource_format_text.cpp:379-384`). Node: every other node heading's `instance=` and
 * every node's value. An orphaned node counts: Godot loads it, re-parented to the root (`packed_scene.cpp:208-215`).
 */
export function extResourceUses(scene) {
    const uses = new Map();
    addIdsIn(scene.connections.flatMap(({ binds }) => (binds === undefined ? [] : [binds])), 'aborting', uses);
    addIdsIn(scene.instancesOutsideNodeBody, 'aborting', uses);
    for (const resource of scene.internalResources)
        addIdsIn(Object.values(resource.data), 'aborting', uses);
    if (scene.mainResource)
        addIdsIn(Object.values(scene.mainResource.data), 'aborting', uses);
    for (const node of scene.nodes)
        addNodeIds(node, uses);
    for (const { node } of scene.orphanedNodes)
        addNodeIds(node, uses);
    return uses;
}
//# sourceMappingURL=usedExtResources.js.map